import { Injectable, inject } from '@angular/core';
import {
    BehaviorSubject,
    Subscription,
    combineLatest,
    debounceTime,
    lastValueFrom,
    map,
    switchMap,
} from 'rxjs';
import { AgentApiService, UserApiService } from '../services';
import { debounceTimes } from '../constants';
import { Agent, AspNetUser } from '../models';
import { AuthToken } from '../configs';

@Injectable({
    providedIn: 'root',
})
export class UserState {
    authService = inject(AuthToken);
    agentApiService = inject(AgentApiService);
    userApiService = inject(UserApiService);
    profileIsLoading$ = new BehaviorSubject<boolean>(false);
    isLoading$ = combineLatest([
        this.authService.authInProgress$,
        this.profileIsLoading$,
    ]).pipe(
        debounceTime(debounceTimes.apiDebounce), // prevent firing too often when each observable event is triggered close to each other
        map(([authIsLoading, profileIsLoading]) => {
            return authIsLoading || profileIsLoading;
        })
    );

    user$ = this.authService.user$;
    aspNetUser$ = new BehaviorSubject<AspNetUser | undefined>(undefined);
    agentDetails$ = new BehaviorSubject<Agent | undefined>(undefined);

    private userSubscription: Subscription | undefined = undefined;

    listenToUserChanges(): void {
        if (this.userSubscription) {
            return;
        }
        this.userSubscription = this.user$.subscribe((user) => {
            if (user) {
                this.loadAgentDetails();
                this.loadAspNetUser();
            } else {
                this.aspNetUser$.next(undefined);
                this.agentDetails$.next(undefined);
            }
        });
    }

    private loadAgentDetails(): void {
        lastValueFrom(
            this.agentApiService.getPartnerId().pipe(
                map((res) => res.data),
                switchMap((partnerId: number) =>
                    this.agentApiService
                        .getAgentDetails(partnerId)
                        .pipe(map((res) => res.data))
                )
            )
        )
            .then((agent) => {
                this.agentDetails$.next(agent);
            })
            .catch(() => {
                this.agentDetails$.next(undefined);
            });
    }

    private loadAspNetUser(): void {
        lastValueFrom(
            this.userApiService.getAspNetUser().pipe(map((res) => res.data))
        )
            .then((user) => {
                this.aspNetUser$.next(user);
            })
            .catch(() => {
                this.aspNetUser$.next(undefined);
            });
    }
}
